import {
	ActionButton,
	ColorArea,
	ColorSlider,
	Content,
	DialogTrigger,
	DropZone,
	Heading,
	IllustratedMessage,
	Popover,
	ProgressCircle,
	TextField,
} from '@react-spectrum/s2';
import Add from '@react-spectrum/s2/icons/Add';
import Close from '@react-spectrum/s2/icons/Close';
import CloudUpload from '@react-spectrum/s2/illustrations/gradient/generic1/CloudUpload';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import {
	type CSSProperties,
	memo,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import type { DropEvent } from 'react-aria';
import {
	Button as AriaButton,
	type Color,
	parseColor,
} from 'react-aria-components';

import {
	IMAGE_FILE_INPUT_ACCEPT,
	isAcceptedImageDropType,
} from '@/api/fileUploadApi';
import { useUserId } from '@/auth/UserIdContext';
import { AnimateIn } from '@/components/AnimateIn';
import { BlobImage } from '@/components/blob/BlobImage';
import { EditableTagList } from '@/components/EditableTagList';
import { useEditableTagList } from '@/hooks/useEditableTagList';
import { useImageUpload, useImageUploadReset } from '@/hooks/useImageUpload';
import { useTagImage } from '@/hooks/useTagImage';
import { trackEvent } from '@/utils/fullstory';

import type {
	BusinessProfile,
	ImageUrl,
} from '../../../../../bridge/types/business-profile.ts';
import { EditableCard } from '../EditableCard';
import { CARD, cardTitleStyles, PILL_TAG } from '../styles';

import {
	applyImageTagsToDraft,
	resolveImageryEntries,
	resolveImageryUrls,
} from './imageryUrls';

function parseColorSafe(value: string): Color {
	try {
		return parseColor(value).toFormat('hsl');
	} catch {
		return parseColor('hsl(0, 0%, 100%)');
	}
}

function colorToHex(color: Color): string {
	return color.toString('hex');
}

const ColorPickerSwatch = ({
	hex,
	onChange,
}: {
	hex: string;
	onChange: (hex: string) => void;
}) => {
	const [color, setColor] = useState<Color>(() => parseColorSafe(hex));

	useEffect(() => {
		setColor(parseColorSafe(hex));
	}, [hex]);

	const handleChange = (newColor: Color) => {
		const hsl = newColor.toFormat('hsl');
		setColor(hsl);
		onChange(colorToHex(hsl));
	};

	return (
		<DialogTrigger>
			<AriaButton
				aria-label="Pick color"
				className="h-10 w-10 shrink-0 cursor-pointer rounded-lg border-none
					outline-none focus-visible:outline-2 focus-visible:outline-offset-2
					focus-visible:outline-[var(--text-primary)]"
				style={{ backgroundColor: hex || '#ffffff' }}
			/>
			<Popover placement="bottom start">
				<div className="flex flex-col gap-2 p-3">
					<ColorArea
						value={color}
						onChange={handleChange}
						xChannel="saturation"
						yChannel="lightness"
					/>
					<ColorSlider value={color} onChange={handleChange} channel="hue" />
					<div className="text-center text-xs text-[var(--text-muted)]">
						{colorToHex(color)}
					</div>
				</div>
			</Popover>
		</DialogTrigger>
	);
};

type VisualIdentityTabProps = {
	profile: BusinessProfile | undefined;
	onSave: (path: string, value: string) => void;
	onArraySave: (path: string, value: string[]) => void;
	onImageUrlsSave: (value: ImageUrl[], baseValue: ImageUrl[]) => void;
};

export const VisualIdentityTab = memo(function VisualIdentityTab({
	profile,
	onSave,
	onArraySave,
	onImageUrlsSave,
}: VisualIdentityTabProps) {
	const userId = useUserId();
	const [logoError, setLogoError] = useState(false);
	const logoUrl = profile?.brand_identity?.logo?.value;
	const colors = (profile?.brand_identity?.colors?.value ?? []).map((c) =>
		c.startsWith('#') ? c : `#${c}`,
	);
	const imageryUrls = resolveImageryUrls(profile?.brand_identity);
	const fonts = profile?.brand_identity?.fonts?.value ?? [];

	// --- Logo ---
	const [logoEditing, setLogoEditing] = useState(false);
	const logoUpload = useImageUpload(userId);
	useImageUploadReset(logoUpload, logoUrl);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleLogoSave = useCallback(() => {
		if (logoUpload.url) {
			onSave('brand_identity.logo', logoUpload.url);
		}
		setLogoEditing(false);
	}, [logoUpload.url, onSave]);

	const handleLogoCancel = useCallback(() => {
		setLogoEditing(false);
		logoUpload.reset();
	}, [logoUpload]);

	const handleLogoEdit = useCallback(() => {
		setLogoEditing(true);
	}, []);

	const handleLogoRemove = useCallback(() => {
		onSave('brand_identity.logo', '');
		logoUpload.reset();
	}, [onSave, logoUpload]);

	const handleLogoFileInput = useCallback(
		async (file: File) => {
			setLogoError(false);
			await logoUpload.handleFile(file);
			if (!logoEditing) {
				setLogoEditing(true);
			}
		},
		[logoUpload, logoEditing],
	);

	const handleLogoDrop = useCallback(
		async (e: DropEvent) => {
			const item = e.items[0];
			if (!item || item.kind !== 'file') return;
			const file = await item.getFile();
			await handleLogoFileInput(file);
		},
		[handleLogoFileInput],
	);

	// --- Imagery ---
	const [imageryEditing, setImageryEditing] = useState(false);
	const [imageryDraft, setImageryDraft] = useState<ImageUrl[]>([]);
	// Snapshot the array as of edit-start so a save can tell "the user removed
	// this" (present in the base, absent from the submission) apart from "someone
	// else added this after I started editing" (present live, absent from the
	// base) -- see onImageUrlsSave.
	const imageryBaseRef = useRef<ImageUrl[]>([]);
	const imageryUpload = useImageUpload(userId);
	const tagImage = useTagImage(userId);
	const imageryFileInputRef = useRef<HTMLInputElement>(null);

	const handleImageryEdit = useCallback(() => {
		const entries = resolveImageryEntries(profile?.brand_identity);
		setImageryDraft(entries);
		imageryBaseRef.current = entries;
		setImageryEditing(true);
	}, [profile?.brand_identity]);

	const handleImagerySave = useCallback(() => {
		onImageUrlsSave(imageryDraft, imageryBaseRef.current);
		setImageryEditing(false);
		imageryUpload.reset();
	}, [onImageUrlsSave, imageryDraft, imageryUpload]);

	const handleImageryCancel = useCallback(() => {
		setImageryEditing(false);
		imageryUpload.reset();
	}, [imageryUpload]);

	const handleImageryFileInput = useCallback(
		async (file: File) => {
			const url = await imageryUpload.handleFile(file);
			if (url) {
				setImageryDraft((d) => [...d, { url, alt_text: '', source: 'upload' }]);
				trackEvent('Profile Imagery Added');
			}
			imageryUpload.reset();
			if (!url) return;
			// ADO-1268: tag the photo so it is contextually reusable, then fold the
			// result into the draft — the existing whole-array save carries it, so
			// tagging never becomes a second writer racing that save. The thumbnail
			// is already on screen; this only enriches the pending entry.
			const tagged = await tagImage(url);
			setImageryDraft((d) => applyImageTagsToDraft(d, url, tagged));
		},
		[imageryUpload, tagImage],
	);

	const handleImageryDrop = useCallback(
		async (e: DropEvent) => {
			const item = e.items[0];
			if (!item || item.kind !== 'file') return;
			const file = await item.getFile();
			await handleImageryFileInput(file);
		},
		[handleImageryFileInput],
	);

	const handleImageryRemove = useCallback((index: number) => {
		setImageryDraft((d) => d.filter((_, i) => i !== index));
		trackEvent('Profile Imagery Removed', { index });
	}, []);

	// --- Fonts ---
	const fontTags = useEditableTagList({
		serverValue: fonts,
		onSave: (values) => onArraySave('brand_identity.fonts', values),
	});

	// --- Colors ---
	const colorTags = useEditableTagList({
		serverValue: colors,
		onSave: (values) => onArraySave('brand_identity.colors', values),
	});

	// Logo DropZone component for both empty state and edit mode
	const LogoDropZone = ({
		isFilled,
		showImage,
	}: {
		isFilled: boolean;
		showImage?: string | null;
	}) => (
		<DropZone
			onDrop={handleLogoDrop}
			getDropOperation={(types) =>
				isAcceptedImageDropType(types) ? 'copy' : 'cancel'
			}
			isFilled={isFilled}
			replaceMessage="Drop file to replace"
		>
			<div
				className="flex cursor-pointer flex-col items-center"
				onClick={() => fileInputRef.current?.click()}
			>
				{logoUpload.isUploading ? (
					<div className="flex h-32 w-full items-center justify-center">
						<ProgressCircle aria-label="Uploading logo" isIndeterminate />
					</div>
				) : showImage ? (
					<BlobImage
						src={showImage}
						alt="Logo preview"
						className="h-20 w-20 rounded-lg object-contain"
					/>
				) : (
					<IllustratedMessage>
						<CloudUpload />
						<Heading>Drag and drop</Heading>
						<Content>Or click to browse</Content>
					</IllustratedMessage>
				)}
			</div>
			<input
				ref={fileInputRef}
				type="file"
				accept={IMAGE_FILE_INPUT_ACCEPT}
				className="hidden"
				onChange={(e) => {
					const file = e.target.files?.[0];
					if (file) handleLogoFileInput(file);
					e.target.value = '';
				}}
			/>
		</DropZone>
	);

	return (
		<div className="grid grid-cols-2 gap-[22px]">
			{/* Left column */}
			<div className="flex flex-col gap-[22px]">
				<AnimateIn delay={0}>
					{/* Logo card - different UX based on state */}
					{logoUrl && !logoError ? (
						<EditableCard
							title="Logo"
							isEditing={logoEditing}
							onEdit={handleLogoEdit}
							onSave={handleLogoSave}
							onCancel={handleLogoCancel}
							onRemove={handleLogoRemove}
						>
							{logoEditing ? (
								<LogoDropZone
									isFilled={true}
									showImage={logoUpload.preview || logoUrl}
								/>
							) : (
								<BlobImage
									src={logoUpload.preview || logoUrl}
									alt="Business logo"
									className="mx-auto h-20 w-20 rounded-lg object-contain"
									onError={() => setLogoError(true)}
								/>
							)}
						</EditableCard>
					) : logoUpload.preview ? (
						<EditableCard
							title="Logo"
							isEditing={logoEditing}
							onEdit={handleLogoEdit}
							onSave={handleLogoSave}
							onCancel={handleLogoCancel}
							onRemove={handleLogoRemove}
						>
							{logoEditing ? (
								<LogoDropZone isFilled={true} showImage={logoUpload.preview} />
							) : (
								<BlobImage
									src={logoUpload.preview}
									alt="Business logo"
									className="mx-auto h-20 w-20 rounded-lg object-contain"
								/>
							)}
						</EditableCard>
					) : (
						<div data-card-title="Logo" className={CARD}>
							<div className="mb-4">
								<Heading level={3} styles={cardTitleStyles}>
									Logo
								</Heading>
							</div>
							<LogoDropZone isFilled={false} showImage={null} />
						</div>
					)}
				</AnimateIn>

				<AnimateIn delay={0.5}>
					<EditableCard
						title="Fonts"
						isEditing={fontTags.isEditing}
						onEdit={fontTags.onEdit}
						onSave={fontTags.onSave}
						onCancel={fontTags.onCancel}
					>
						{fontTags.isEditing ? (
							<EditableTagList tags={fontTags} placeholder="Add font" />
						) : (
							<div className="flex flex-wrap gap-2">
								{fontTags.displayTags.map((font) => (
									<span key={font} className={PILL_TAG}>
										{font}
									</span>
								))}
								{fontTags.displayTags.length === 0 && (
									<span className="text-sm text-[var(--text-muted)]">
										No fonts yet
									</span>
								)}
							</div>
						)}
					</EditableCard>
				</AnimateIn>

				<AnimateIn delay={1} className="flex flex-1 flex-col">
					<EditableCard
						title="Colors"
						isEditing={colorTags.isEditing}
						onEdit={colorTags.onEdit}
						onSave={colorTags.onSave}
						onCancel={colorTags.onCancel}
						className="h-full"
					>
						{colorTags.isEditing ? (
							<EditableTagList
								tags={colorTags}
								placeholder="#000000"
								renderEditRow={({ tag, index, onUpdate, onRemove }) => (
									<div key={index} className="flex w-full items-center gap-2">
										<ColorPickerSwatch
											hex={tag}
											onChange={(hex) => onUpdate(index, hex)}
										/>
										<span className="text-sm text-[var(--text-muted)]">#</span>
										<div className="flex-1">
											<TextField
												aria-label="Color hex"
												value={tag.replace(/^#/, '')}
												onChange={(val) =>
													onUpdate(index, `#${val.replace(/^#/, '')}`)
												}
											/>
										</div>
										<ActionButton
											aria-label={`Remove ${tag}`}
											isQuiet
											size="XS"
											onPress={() => onRemove(index)}
										>
											<Close />
										</ActionButton>
									</div>
								)}
								renderAddRow={({ value, onChange, onAdd }) => (
									<div className="flex w-full items-center gap-2">
										<ColorPickerSwatch hex={value || ''} onChange={onChange} />
										<span className="text-sm text-[var(--text-muted)]">#</span>
										<div className="flex-1">
											<TextField
												aria-label="New color hex"
												value={(value || '').replace(/^#/, '')}
												onChange={(val) =>
													onChange(`#${val.replace(/^#/, '')}`)
												}
												placeholder="000000"
											/>
										</div>
										<ActionButton
											aria-label="Add color"
											isQuiet
											size="XS"
											onPress={onAdd}
										>
											<Add />
										</ActionButton>
									</div>
								)}
							/>
						) : (
							<div className="grid grid-cols-2 gap-4">
								{colorTags.displayTags.map((hex) => (
									<div
										key={hex}
										className="flex items-center gap-2 rounded-2xl bg-[#e9e9e9]
											px-4 py-3"
									>
										<div
											className="h-12 w-12 shrink-0 rounded-lg"
											style={{ backgroundColor: hex }}
										/>
										<span className="text-xs font-medium text-[#292929]">
											{hex}
										</span>
									</div>
								))}
								{colorTags.displayTags.length === 0 && (
									<span className="text-sm text-[var(--text-muted)]">
										No colors yet
									</span>
								)}
							</div>
						)}
					</EditableCard>
				</AnimateIn>
			</div>

			{/* Right column */}
			<AnimateIn delay={1} className="h-full">
				<EditableCard
					title="Imagery"
					className="h-full"
					isEditing={imageryEditing}
					onEdit={handleImageryEdit}
					onSave={handleImagerySave}
					onCancel={handleImageryCancel}
					isSaveDisabled={imageryUpload.isUploading}
				>
					{imageryEditing ? (
						<div className="flex flex-col gap-3">
							<DropZone
								size="S"
								onDrop={handleImageryDrop}
								getDropOperation={(types) =>
									isAcceptedImageDropType(types) ? 'copy' : 'cancel'
								}
								styles={style({ width: 'full' })}
							>
								<div
									className="flex w-full cursor-pointer flex-col items-center
										justify-center"
									onClick={() => {
										if (!imageryUpload.isUploading)
											imageryFileInputRef.current?.click();
									}}
								>
									{imageryUpload.isUploading ? (
										<ProgressCircle
											aria-label="Uploading image"
											isIndeterminate
										/>
									) : (
										<IllustratedMessage>
											<CloudUpload />
											<Heading>Drag and drop</Heading>
											<Content>Or click to browse</Content>
										</IllustratedMessage>
									)}
								</div>
								<input
									ref={imageryFileInputRef}
									type="file"
									accept={IMAGE_FILE_INPUT_ACCEPT}
									className="hidden"
									onChange={(e) => {
										const file = e.target.files?.[0];
										if (file) handleImageryFileInput(file);
										e.target.value = '';
									}}
								/>
							</DropZone>
							<div className="columns-2 gap-3">
								{imageryDraft
									.map((img, i) => ({ img, i }))
									.reverse()
									.map(({ img, i }, j) => (
										<div
											key={img.url}
											className="group/tile relative mb-3 break-inside-avoid"
										>
											<BlobImage
												src={img.url}
												alt={`Brand imagery ${j + 1}`}
												className="w-full rounded-lg"
											/>
											<AriaButton
												aria-label={`Remove image ${j + 1}`}
												onPress={() => handleImageryRemove(i)}
												style={{ '--iconPrimary': '#ffffff' } as CSSProperties}
												className="absolute top-2 right-2 flex size-7
													cursor-pointer items-center justify-center rounded-lg
													border-none bg-black/60 opacity-0 transition-opacity
													outline-none group-hover/tile:opacity-100
													hover:bg-black/80 focus-visible:opacity-100
													focus-visible:outline-2 focus-visible:outline-white"
											>
												<Close
													UNSAFE_style={{ width: '1rem', height: '1rem' }}
												/>
											</AriaButton>
										</div>
									))}
							</div>
						</div>
					) : (
						<div className="columns-2 gap-3">
							{[...imageryUrls].reverse().map((url, i) => (
								<BlobImage
									key={url}
									src={url}
									alt={`Brand imagery ${i + 1}`}
									className="mb-3 w-full break-inside-avoid rounded-lg"
								/>
							))}
							{imageryUrls.length === 0 && (
								<span className="text-sm text-[var(--text-muted)]">
									No imagery yet
								</span>
							)}
						</div>
					)}
				</EditableCard>
			</AnimateIn>
		</div>
	);
});
